import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type FieldErrors = { email?: string; password?: string };

// The zod schema the Next form validated with, as the two checks it actually made.
// The messages are part of what the page renders, so they are kept verbatim.
function validate(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {};

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Invalid email address';
  }
  if (password.length < 1) {
    errors.password = 'Password is required';
  }

  return errors;
}

// Port of app/admin/login/page.tsx. It signs in against /api/auth/sign-in/email — the
// endpoint the page's better-auth client called — and then lands on /admin.
export default function Show() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const fieldErrors = validate(email, password);
    setErrors(fieldErrors);

    if (fieldErrors.email || fieldErrors.password) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch('/api/auth/sign-in/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, callbackURL: '/admin' }),
      });
      const body = (await response.json()) as { message?: string };

      if (!response.ok) {
        console.error('Admin login failed', { email, error: body });
        toast.error(
          body.message || 'Login failed. Check credentials and try again.',
        );
        return;
      }

      toast.success('Successfully signed in');
      window.location.href = '/admin';
    } catch (unexpected) {
      console.error('Admin login unexpected error', { email, error: unexpected });
      toast.error(
        unexpected instanceof Error
          ? unexpected.message
          : 'An unexpected error occurred during login',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Admin Login</CardTitle>
          <CardDescription>Sign in with your admin credentials</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="admin-login-email">Email</Label>
              <Input
                id="admin-login-email"
                name="email"
                type="email"
                placeholder="admin@example.com"
                disabled={isLoading}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {errors.email && (
                <p className="text-destructive text-sm">{errors.email}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="admin-login-password">Password</Label>
              <Input
                id="admin-login-password"
                name="password"
                type="password"
                placeholder="Enter your password"
                disabled={isLoading}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {errors.password && (
                <p className="text-destructive text-sm">{errors.password}</p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <p className="mt-4 text-center text-muted-foreground text-sm">
            Only authorized administrators can access this panel
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
